import { useEffect, useState, useCallback } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Activity, KeyRound, User as UserIcon, LogOut, Users, ShieldCheck,
  Building2, Loader2, Webhook, Lock, TrendingUp, Bell, FileDown, BookOpen,
} from "lucide-react";
import {
  partnerApi, clearPartnerSession, formatPartnerError,
  isPartnerLoggedIn, PARTNER_ROLE_LABELS,
} from "./partnerApi";
import "./PartnerPortal.css";

const NAV = [
  { to: "/partner/portal", label: "Overview", icon: Activity, end: true, testid: "pp-nav-overview" },
  { to: "/partner/portal/health", label: "Health", icon: ShieldCheck, testid: "pp-nav-health" },
  { to: "/partner/portal/analytics", label: "Analytics", icon: TrendingUp, testid: "pp-nav-analytics" },
  { to: "/partner/portal/api-keys", label: "API Keys", icon: KeyRound, testid: "pp-nav-keys" },
  { to: "/partner/portal/oauth", label: "OAuth", icon: Lock, testid: "pp-nav-oauth" },
  { to: "/partner/portal/webhooks", label: "Webhooks", icon: Webhook, testid: "pp-nav-webhooks" },
  { to: "/partner/portal/notifications", label: "Notifications", icon: Bell, testid: "pp-nav-notifications" },
  { to: "/partner/portal/reports", label: "Reports", icon: FileDown, testid: "pp-nav-reports" },
  { to: "/partner/portal/profile", label: "Profile", icon: Building2, testid: "pp-nav-profile" },
  { to: "/partner/portal/users", label: "Users", icon: Users, testid: "pp-nav-users" },
  { to: "/partner/portal/trust-center", label: "Trust Center", icon: BookOpen, testid: "pp-nav-trust-center" },
  { to: "/partner/portal/security", label: "Security", icon: ShieldCheck, testid: "pp-nav-security" },
];

export default function PartnerPortalLayout() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!isPartnerLoggedIn()) { nav("/partner/login", { replace: true }); return; }
    setLoading(true);
    try {
      const { data } = await partnerApi.get("/partner/portal/me");
      setMe(data);
    } catch (e) {
      setError(formatPartnerError(e));
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  const logout = () => {
    clearPartnerSession();
    nav("/partner/login", { replace: true });
  };

  if (loading) {
    return (
      <div className="hw-pp" data-testid="partner-portal-loading">
        <div style={{ minHeight: "60vh", display: "grid", placeItems: "center", color: "#5EEAD4" }}>
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !me) {
    return (
      <div className="hw-pp__auth-shell">
        <div className="hw-pp__auth-card">
          <div className="hw-pp__eyebrow">Partner Portal</div>
          <h1 className="hw-pp__auth-title">Unable to load your account</h1>
          <p className="hw-pp__auth-sub">{error || "Session error."}</p>
          <button onClick={logout} className="hw-pp__btn hw-pp__btn--full">Sign in again</button>
        </div>
      </div>
    );
  }

  const role = me.partner_user.partner_role || "developer";

  return (
    <div className="hw-pp" data-testid="partner-portal-shell">
      <div className="hw-pp__shell">
        <aside className="hw-pp__side">
          <div>
            <div className="hw-pp__brand">HarborWell · Partner Portal</div>
            <div className="hw-pp__org" data-testid="pp-org-name">{me.partner_org.name}</div>
            <div className="hw-pp__org-slug">{me.partner_org.slug}</div>
          </div>

          <nav className="hw-pp__nav">
            {NAV.map(({ to, label, icon: Icon, end, testid }) => (
              <NavLink key={to} to={to} end={end} data-testid={testid}
                        className={({ isActive }) =>
                          `hw-pp__nav-item ${isActive ? "hw-pp__nav-item--active" : ""}`}>
                <Icon className="w-4 h-4" />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="hw-pp__side-footer">
            <strong data-testid="pp-user-email">{me.partner_user.email}</strong>
            <div>{me.partner_user.name || <em style={{ opacity: 0.7 }}>No name set</em>}</div>
            <span className="hw-pp__role-badge" data-testid="pp-user-role">
              {PARTNER_ROLE_LABELS[role] || role}
            </span>
            <button onClick={logout} data-testid="pp-logout"
                     className="hw-pp__btn"
                     style={{ marginTop: "0.85rem", width: "100%", padding: "0.4rem" }}>
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </aside>

        <main className="hw-pp__main">
          <Outlet context={{ me, reloadMe: load }} />
        </main>
      </div>
    </div>
  );
}
